package com.iptvplayer.shared.di

import com.iptvplayer.shared.data.local.cache.ContentCache
import com.iptvplayer.shared.data.local.preferences.TokenStorage
import com.iptvplayer.shared.data.parser.M3UParser
import com.iptvplayer.shared.data.remote.api.*
import com.iptvplayer.shared.data.remote.configurePlatformProxy
import com.iptvplayer.shared.data.remote.dto.RefreshRequestDto
import com.iptvplayer.shared.data.remote.xtream.XtreamApiClient
import com.iptvplayer.shared.data.repository.*
import com.iptvplayer.shared.domain.repository.*
import com.iptvplayer.shared.presentation.viewmodel.*
import com.iptvplayer.shared.util.Constants
import io.ktor.client.*
import io.ktor.client.engine.HttpClientEngine
import io.ktor.client.plugins.*
import io.ktor.client.plugins.auth.*
import io.ktor.client.plugins.auth.providers.*
import io.ktor.client.plugins.contentnegotiation.*
import io.ktor.client.plugins.logging.*
import io.ktor.client.plugins.websocket.*
import io.ktor.serialization.kotlinx.json.*
import kotlinx.serialization.json.Json
import org.koin.core.module.Module
import org.koin.dsl.module

expect val platformModule: Module

val commonModule = module {

    // ──────────────────── JSON ────────────────────

    single {
        Json {
            ignoreUnknownKeys = true
            isLenient = true
            encodeDefaults = true
            prettyPrint = false
            coerceInputValues = true
        }
    }

    // ──────────────────── Ktor HttpClient ────────────────────

    single {
        val proxyTokenStorage = get<TokenStorage>()
        HttpClient(get<HttpClientEngine>()) {
            // Web-only: route external (IPTV panel) requests through /api/proxy.
            configurePlatformProxy(proxyTokenStorage)

            install(ContentNegotiation) {
                json(get<Json>())
            }

            install(Logging) {
                logger = Logger.DEFAULT
                // Never log bodies/headers: requests carry bearer tokens and
                // IPTV credentials. Use Chucker (debug builds) for inspection.
                level = LogLevel.NONE
            }

            // Watch Together rooms (chat + playback sync) ride on WebSockets.
            install(WebSockets)

            install(Auth) {
                bearer {
                    loadTokens {
                        val tokenStorage = get<TokenStorage>()
                        val token = tokenStorage.getToken()
                        if (token != null) {
                            BearerTokens(token.accessToken, token.refreshToken)
                        } else {
                            null
                        }
                    }

                    refreshTokens {
                        val tokenStorage = get<TokenStorage>()
                        val authApi = AuthApi(client)
                        val oldToken = tokenStorage.getToken()
                        if (oldToken != null) {
                            try {
                                val response = authApi.refreshToken(
                                    RefreshRequestDto(oldToken.refreshToken)
                                )
                                val newToken = com.iptvplayer.shared.domain.model.AuthToken(
                                    accessToken = response.accessToken,
                                    refreshToken = response.refreshToken,
                                    expiresAt = response.expiresAt
                                )
                                tokenStorage.saveToken(newToken)
                                BearerTokens(newToken.accessToken, newToken.refreshToken)
                            } catch (_: Exception) {
                                tokenStorage.clear()
                                null
                            }
                        } else {
                            null
                        }
                    }
                }
            }

            install(HttpTimeout) {
                // Bumped from 30s — the Xtream series catalog can be ~10 MB+
                // on large panels, and the JS client's receive+parse takes a
                // while; we'd otherwise hit BodyStreamBuffer-aborted mid-fetch.
                requestTimeoutMillis = 90_000
                connectTimeoutMillis = 15_000
                socketTimeoutMillis = 90_000
            }

            defaultRequest {
                url(Constants.BASE_URL)
            }
        }
    }

    // ──────────────────── API Services ────────────────────

    single { AuthApi(get()) }
    single { DeviceApi(get()) }
    single { FavoriteApi(get()) }
    single { WatchHistoryApi(get()) }
    single { RemoteCommandApi(get()) }
    single { SourceApi(get()) }
    single { AdminApi(get()) }
    single { WatchApi(get()) }

    // ──────────────────── Xtream & Parser ────────────────────

    single { XtreamApiClient(get()) }
    single { M3UParser() }
    single { ContentCache(get()) }

    // ──────────────────── Repositories ────────────────────

    single<AuthRepository> { AuthRepositoryImpl(get(), get()) }
    single<DeviceRepository> { DeviceRepositoryImpl(get(), get()) }
    single<ContentRepository> { ContentRepositoryImpl(get(), get(), get(), get(), get()) }
    single<FavoriteRepository> { FavoriteRepositoryImpl(get()) }
    single<WatchHistoryRepository> { WatchHistoryRepositoryImpl(get()) }
    single<RemoteCommandRepository> { RemoteCommandRepositoryImpl(get()) }
    single { EpgService(get(), get()) }

    // ──────────────────── ViewModels ────────────────────

    factory { AuthViewModel(get()) }
    factory { BrowsePagingViewModel(get()) }
    factory { DeviceViewModel(get()) }
    factory { FavoritesViewModel(get()) }
    factory { HistoryViewModel(get()) }
    factory { HomeViewModel(get(), get()) }
    factory { PlayerViewModel(get(), get(), get()) }
    factory { RemotePlayViewModel(get(), get()) }
    factory { SourceViewModel(get()) }
    factory { AdminViewModel(get()) }
    factory { WatchTogetherViewModel(get(), get(), get(), get()) }
}
